import { FormGroup, Input, RadioItem, TextArea } from '@agro-club/agroclub-shared'
import { FormikProps } from 'formik'
import { endpoints } from 'modules/endpoints'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FormRadio } from 'views/components/form/FormRadio'
import FormSelectShared from 'views/components/FormSelectShared'
import { SharedModalContent, SharedModalInputs } from 'views/components/SharedModal'
import { ShortFormControls } from 'views/styled/common'
import { FormStorageControl } from './FormStorageControl'
import { labelQuantity } from 'modules/utils/numbers/formatQuantity'

export type ElevatorFormFields = {
  title: string
  address: string | null
  company: string | null
  grain_elevator_railway_station: string | null
  capacity: number | null
  provided_services: string
  acceptance_costs: number | null
  storage_costs: number | null
  storage_costs_unit: string | null
  mechanical_railcar_loading_costs: number | null
  rail_car_capacity: number | null
  expedite_costs: number | null
  documents_costs: number | null
  other_costs: number | null
  is_composite_costs: boolean
  composite_costs: number | null
  comment: string
}

const rubPerTon = (label: string) => `${label}, Р/т`

type Props = {
  formik: FormikProps<ElevatorFormFields>
}

export const ElevatorCommonFields: FC<Props> = ({ formik }) => {
  const { t } = useTranslation('warehouses')

  return (
    <SharedModalContent>
      <SharedModalInputs>
        <FormGroup label={t('common:name')} error={formik.errors.title} required>
          <Input {...formik.getFieldProps('title')} invalid={!!formik.errors.title} />
        </FormGroup>
        <FormSelectShared
          label={t('common:company')}
          endpoint={endpoints.company()}
          formik={formik}
          fieldName="company"
          placeholder=""
          searchField="search"
          required
        />
        <FormSelectShared
          formik={formik}
          fieldName="address"
          getLabel={(dto) => dto.address}
          searchField="address"
          label={t('common:address')}
          disabled={!formik.values.company}
          endpoint={endpoints.companyAddress(formik.values.company || '')}
          simpleApiFormat
          required
        />
        <FormSelectShared
          label={t('railwayStation')}
          formik={formik}
          fieldName="grain_elevator_railway_station"
          getLabel={(station) => `${station.title} (${station.station_code})`}
          endpoint={endpoints.LogisticsKR.railwayStations()}
          searchField="search"
          placeholder=""
        />

        <FormGroup label={labelQuantity(t('capacity'))} error={formik.errors.capacity}>
          <Input {...formik.getFieldProps('capacity')} invalid={!!formik.errors.capacity} type="number" />
        </FormGroup>
        <FormGroup error={formik.errors.comment} label={t('services')}>
          <TextArea
            {...formik.getFieldProps('provided_services')}
            borderRadius="form"
            invalid={!!formik.errors.provided_services}
            height={80}
          />
        </FormGroup>

        <FormGroup label={t('rail_car_capacity')} error={formik.errors.capacity}>
          <Input {...formik.getFieldProps('rail_car_capacity')} invalid={!!formik.errors.capacity} type="number" />
        </FormGroup>

        <FormRadio label={t('costs_type')} formik={formik} field="is_composite_costs">
          <RadioItem value={false} label={t('costs_types.detailed')} />
          <RadioItem value={true} label={t('costs_types.composite')} />
        </FormRadio>
        {formik.values.is_composite_costs ? (
          <>
            <FormGroup label={rubPerTon(t('elevator_costs.composite_costs'))} error={formik.errors.composite_costs}>
              <Input
                {...formik.getFieldProps('composite_costs')}
                invalid={!!formik.errors.composite_costs}
                type="number"
              />
            </FormGroup>
          </>
        ) : (
          <>
            <ShortFormControls>
              <FormGroup
                label={rubPerTon(t('elevator_costs_short.acceptance_costs'))}
                error={formik.errors.acceptance_costs}
              >
                <Input
                  {...formik.getFieldProps('acceptance_costs')}
                  invalid={!!formik.errors.acceptance_costs}
                  type="number"
                />
              </FormGroup>
            </ShortFormControls>
            <ShortFormControls width={530}>
              <FormStorageControl formik={formik} />
            </ShortFormControls>
            <ShortFormControls>
              <FormGroup
                label={rubPerTon(t('elevator_costs_short.mechanical_railcar_loading_costs'))}
                error={formik.errors.mechanical_railcar_loading_costs}
              >
                <Input
                  {...formik.getFieldProps('mechanical_railcar_loading_costs')}
                  invalid={!!formik.errors.mechanical_railcar_loading_costs}
                  type="number"
                />
              </FormGroup>
              <FormGroup
                label={rubPerTon(t('elevator_costs_short.expedite_costs'))}
                error={formik.errors.expedite_costs}
              >
                <Input
                  {...formik.getFieldProps('expedite_costs')}
                  invalid={!!formik.errors.expedite_costs}
                  type="number"
                />
              </FormGroup>
              <FormGroup
                label={rubPerTon(t('elevator_costs_short.documents_costs'))}
                error={formik.errors.documents_costs}
              >
                <Input
                  {...formik.getFieldProps('documents_costs')}
                  invalid={!!formik.errors.documents_costs}
                  type="number"
                />
              </FormGroup>
              <FormGroup label={rubPerTon(t('elevator_costs_short.other_costs'))} error={formik.errors.other_costs}>
                <Input {...formik.getFieldProps('other_costs')} invalid={!!formik.errors.other_costs} type="number" />
              </FormGroup>
            </ShortFormControls>
          </>
        )}
        <FormGroup error={formik.errors.comment} label={t('common:comment')}>
          <TextArea
            {...formik.getFieldProps('comment')}
            borderRadius="form"
            invalid={!!formik.errors.comment}
            height={80}
          />
        </FormGroup>
      </SharedModalInputs>
    </SharedModalContent>
  )
}
